import { useEffect } from "react";
import API from "../utils/api";
import { useAuth } from "../App";

const STORAGE_KEY = "sales_crm_device_notifs_seen";

function readSeen() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]"); } catch { return []; }
}

function writeSeen(ids) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(ids.slice(-120)));
}

async function showDeviceNotification(note) {
  if (!("Notification" in window) || Notification.permission !== "granted") return;
  const title = note.title || "Sales CRM";
  const body = note.subtitle || note.message || "";
  if ("serviceWorker" in navigator) {
    const reg = await navigator.serviceWorker.ready.catch(() => null);
    if (reg?.showNotification) {
      await reg.showNotification(title, {
        body,
        tag: note.id || title,
        icon: "/icon-192.png",
        badge: "/icon-192.png",
        data: { url: "/" },
      });
      return;
    }
  }
  // Android Chrome has no Notification constructor — it throws.
  try { new Notification(title, { body, tag: note.id || title }); } catch { /* unsupported */ }
}

export default function DeviceNotifications() {
  const { isLoggedIn } = useAuth();

  // Permission is requested by useFcmPush.js, which waits for a user gesture.
  // Asking here on mount is ignored outright by Safari/iOS and suppressed by
  // Chrome's quiet-prompt heuristic, and a second prompt would race the first.
  // /sw.js is already registered once in index.js — re-registering it here
  // churns the root-scope registration for no benefit.

  useEffect(() => {
    if (!isLoggedIn) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const res = await API.get("/notifications");
        const notes = res.data?.notifications || [];
        const seen = new Set(readSeen());
        const fresh = notes.filter(n => !n.is_read && n.id && !seen.has(n.id)).slice(0, 5);
        for (const note of fresh) {
          await showDeviceNotification(note);
          seen.add(note.id);
        }
        writeSeen(Array.from(seen));
      } catch {
        // Silent: notifications are supportive, not blocking.
      }
      if (!cancelled) setTimeout(tick, 60000);
    };
    tick();
    return () => { cancelled = true; };
  }, [isLoggedIn]);

  return null;
}
